import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorService, type Vendor } from '../services/vendorService';
import { remoteConfigService } from '../services/remoteConfigService';
import { VendorCard } from '../components/VendorCard';
import { SEO } from '../components/SEO';
import {
  generateOrganizationSchema,
  generateLocalBusinessSchema,
  getBaseUrl,
} from '../utils/seo';
import './VendorsPage.css';

/**
 * SEO-optimized Vendors Page
 */
export function VendorsPage() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        setLoading(true);
        const fetchedVendors = await vendorService.getVendors();
        
        // Filter vendors by remote config and sort alphabetically by name
        const filteredVendors = fetchedVendors
          .filter((vendor) => remoteConfigService.isVendorEnabled(vendor.id))
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setVendors(filteredVendors);
      } catch (error) {
        console.error('Failed to load vendors:', error);
        const cachedVendors = vendorService.getVendorsSync();
        setVendors(
          cachedVendors
            .filter((vendor) => remoteConfigService.isVendorEnabled(vendor.id))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, []);

  const handleStoreDetailsPress = (vendor: Vendor) => {
    navigate(`/vendors/${vendor.id}/details`);
  };

  // Generate structured data
  const structuredData = useMemo(() => {
    const baseUrl = getBaseUrl();
    const data = [
      generateOrganizationSchema(baseUrl),
    ];

    // Add LocalBusiness schema for each vendor
    vendors.forEach((vendor) => {
      data.push(
        generateLocalBusinessSchema({
          name: vendor.name,
          url: vendor.websiteUrl,
          description: vendor.description,
        })
      );
    });

    return data;
  }, [vendors]);

  return (
    <>
      <SEO
        title="Peptide Vendors - Peptide Finder"
        description="Browse trusted peptide vendors with exclusive coupon codes. Find the best deals on research peptides from verified suppliers."
        keywords="peptide vendors, research peptide suppliers, peptide coupons, peptide deals"
        structuredData={structuredData}
      />
      <div className="vendors-page">
        <header className="vendors-page-header">
          <div className="vendors-page-container">
            <h1>Peptide Vendors</h1>
            <p className="vendors-page-subtitle">
              Browse trusted vendors with exclusive coupon codes
            </p>
          </div>
        </header>

        <main className="vendors-page-main">
          <div className="vendors-page-container">
            {loading && vendors.length === 0 ? (
              <div className="vendors-page-loading" role="status" aria-live="polite">
                <p>Loading vendors...</p>
              </div>
            ) : vendors.length === 0 ? (
              <div className="vendors-page-empty">
                <p>No vendors found.</p>
              </div>
            ) : (
              <>
                <section className="vendors-page-list" aria-label="Vendor directory">
                  {vendors.map((vendor) => (
                    <VendorCard
                      key={vendor.id}
                      vendor={vendor}
                      clickTrackingSource="vendors_page"
                      onStoreDetailsPress={() => handleStoreDetailsPress(vendor)}
                    />
                  ))}
                </section>
                <section className="vendors-page-disclosure" aria-label="Affiliate disclosure">
                  <p className="vendors-page-disclosure-text">
                    Some vendors listed are affiliate partners. We may earn a commission from qualifying purchases at no extra cost to you.
                  </p>
                </section>
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
}

